import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BarChart3, LineChart, PieChart, TrendingUp, Users, ShoppingCart } from "lucide-react";
import { DynamicChart } from "./DynamicChart";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TemplateChart {
  id: string;
  type: 'bar' | 'line' | 'pie';
  title: string;
  data: any[];
  xAxis?: string;
  yAxis?: string;
}

interface DashboardTemplate {
  id: string;
  title: string;
  description: string;
  icon: any;
  industry: string;
  charts: TemplateChart[];
}

const templates: DashboardTemplate[] = [
  {
    id: 'sales-analytics',
    title: "Sales Analytics",
    description: "Track revenue, growth, and sales patterns",
    icon: TrendingUp,
    industry: 'ecommerce',
    charts: [
      {
        id: 'revenue-trend',
        type: 'line',
        title: 'Revenue Trend',
        data: [
          { name: 'Jan', value: 45000 },
          { name: 'Feb', value: 52000 },
          { name: 'Mar', value: 48000 },
          { name: 'Apr', value: 61000 },
          { name: 'May', value: 55000 },
          { name: 'Jun', value: 67000 }
        ],
        xAxis: 'name',
        yAxis: 'value'
      },
      {
        id: 'sales-by-category',
        type: 'pie',
        title: 'Sales by Category',
        data: [
          { name: 'Electronics', value: 35 },
          { name: 'Clothing', value: 25 },
          { name: 'Home', value: 20 },
          { name: 'Books', value: 20 }
        ]
      }
    ]
  },
  {
    id: 'customer-insights',
    title: "Customer Insights",
    description: "Analyze customer behavior and segments",
    icon: Users,
    industry: 'general',
    charts: [
      {
        id: 'customer-segments',
        type: 'pie',
        title: 'Customer Segments',
        data: [
          { name: 'New Customers', value: 30 },
          { name: 'Returning', value: 45 },
          { name: 'VIP', value: 25 }
        ]
      },
      {
        id: 'acquisition-trend',
        type: 'line',
        title: 'Customer Acquisition',
        data: [
          { name: 'Week 1', value: 120 },
          { name: 'Week 2', value: 150 },
          { name: 'Week 3', value: 180 },
          { name: 'Week 4', value: 200 }
        ],
        xAxis: 'name',
        yAxis: 'value'
      }
    ]
  },
  {
    id: 'performance-metrics',
    title: "Performance Metrics",
    description: "Monitor KPIs and business metrics",
    icon: BarChart3,
    industry: 'general',
    charts: [
      {
        id: 'kpi-comparison',
        type: 'bar',
        title: 'KPI Performance',
        data: [
          { name: 'Sales Target', value: 85 },
          { name: 'Customer Satisfaction', value: 92 },
          { name: 'Product Quality', value: 88 },
          { name: 'Delivery Time', value: 90 }
        ],
        xAxis: 'name',
        yAxis: 'value'
      }
    ]
  }
];

export const DashboardTemplates = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<DashboardTemplate | null>(null);
  const [appliedCharts, setAppliedCharts] = useState<TemplateChart[]>([]);
  const [dataSources, setDataSources] = useState<any[]>([]);

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_folder', false)
        .limit(5);

      if (error) throw error;
      setDataSources(data || []);
    } catch (error) {
      console.error('Error loading data sources:', error);
    }
  };

  const handleTemplateSelect = (template: DashboardTemplate) => {
    setSelectedTemplate(template);
    setAppliedCharts(template.charts);
    toast.success(`${template.title} template applied with ${template.charts.length} charts`);
  };

  const clearTemplate = () => {
    setSelectedTemplate(null);
    setAppliedCharts([]);
    toast.info('Template cleared');
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Dashboard Templates</h2>
          {selectedTemplate && (
            <Button variant="outline" onClick={clearTemplate}>
              Clear Template
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {templates.map((template) => (
            <Card
              key={template.id}
              className={`p-4 hover:bg-accent cursor-pointer transition-colors border-2 ${
                selectedTemplate?.id === template.id ? 'border-primary' : 'border-transparent'
              }`}
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="flex items-start space-x-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <template.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{template.title}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {template.charts.length} charts
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {template.description}
                  </p>
                  <Badge variant="outline" className="text-xs mt-2">
                    {template.industry}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {dataSources.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-sm mb-2">Your Data Sources ({dataSources.length})</h4>
            <div className="flex gap-2 flex-wrap">
              {dataSources.map(source => (
                <Badge key={source.id} variant="outline" className="text-xs">
                  {source.file_name}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Templates will automatically use your uploaded data when available
            </p>
          </div>
        )}
      </Card>

      {/* Applied Template Charts */}
      {appliedCharts.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Template: {selectedTemplate?.title}</h3>
            <Badge variant="default">{appliedCharts.length} charts</Badge>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {appliedCharts.map(chart => (
              <DynamicChart
                key={chart.id}
                config={{
                  id: chart.id,
                  type: chart.type,
                  title: chart.title,
                  data: chart.data,
                  xAxis: chart.xAxis,
                  yAxis: chart.yAxis
                }}
                data={chart.data}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};